import { CreditScore, InsurancePlan, InsurancePlanTier, VehiclePlan } from '../types';
import { Form } from 'react-router';
import { isEmpty } from 'lodash-es';
import { LoaderButton } from '@/common/components/loader-button';
import { PaymentMethodConfiguration } from '@/account/types/payment-method-configuration';
import { PDPErrors, PaymentErrors } from '../types/pdp-action';
import { SubscriptionCFA } from '@/account/types/subscription';
import CannotOrderPayOverdueBalance from './pdp/plan-selection-panel/cannot-order-pay-overdue-balance';
import PDPBuildMonthlyPackage from './pdp-build-monthly-package';
import PDPSelectMonthlyPlan from './pdp-select-monthly-plan';
import Spacer from '@/common/components/spacer';
import StatusBanner from '@/common/components/status-banner';
import SwapDetailsPane from './pdp/swap-details-pane';
import useCanOrder from '../hooks/use-can-order';
import useHasUnpaidBalance from '@/account/hooks/use-has-unpaid-balance';
import { StripeConfig } from '@/checkout/components/stripe-checkout-form';
import { AgeRange } from '../types/age-range';
import CannotOrder from './pdp/plan-selection-panel/cannot-order';

interface PDPDetailsPaneProps {
  subscription?: SubscriptionCFA;
  vehiclePlans: VehiclePlan[];
  insurancePlans?: InsurancePlan[];
  mostPopularPlanMileageAmount?: string;
  currentPlanMiles?: number;
  currentInsurancePlanTier?: InsurancePlanTier;
  isOrderable: boolean;
  errors?: PDPErrors;
  unpaidBalance: {
    amount?: number;
    paymentMethodConfiguration?: PaymentMethodConfiguration;
    ADYEN_ORIGIN_KEY: string;
    ADYEN_JS_SRC: string;
    SMARTYSTREETS_EMBEDDED_KEY: string;
    paymentErrors?: PaymentErrors;
    updatePaymentErrors?: PaymentErrors;
    shouldUseStripe?: boolean;
    stripeConfig?: StripeConfig;
  };
  // CreditAgeChangeDrawerDialog props
  creditModalOpen: boolean;
  handleCloseCreditModal: () => void;
  handleOpenCreditModal: () => void;
  // Credit
  creditScores: CreditScore[];
  ageRanges?: AgeRange[];
  selectedIsOver25: boolean;
  selectedCreditScoreId?: string;
  selectedAgeRangeId?: string;
  selectedMileagePlan?: number;
  selectedInsurancePlan?: string;
  shouldBreakoutInsurance?: boolean;
  enableInsuranceChange?: boolean;
  fullstoryContinueEventDelegate: () => void;
  fullstoryPlanEventDelegate: () => void;
  fullstoryInsuranceEventDelegate: () => void;
  price_savings_1200_over_850?: number;
  price_per_mile_850_plan_for_1200_miles?: number;
  price_difference_850_vs_1200?: number;
  mostPopularPlanMileagePercentage?: string;
}

export default function PDPDetailsPane({
  isOrderable,
  subscription,
  vehiclePlans,
  insurancePlans,
  mostPopularPlanMileageAmount,
  currentPlanMiles,
  currentInsurancePlanTier,
  errors,
  unpaidBalance,
  // CreditAgeChangeDrawerDialog props
  creditModalOpen,
  handleCloseCreditModal,
  handleOpenCreditModal,
  // Credit
  creditScores,
  ageRanges,
  selectedIsOver25,
  selectedCreditScoreId,
  selectedAgeRangeId,
  selectedMileagePlan,
  selectedInsurancePlan,
  shouldBreakoutInsurance,
  enableInsuranceChange,
  fullstoryContinueEventDelegate,
  fullstoryPlanEventDelegate,
  fullstoryInsuranceEventDelegate,
  price_savings_1200_over_850,
  price_per_mile_850_plan_for_1200_miles,
  price_difference_850_vs_1200,
  mostPopularPlanMileagePercentage,
}: PDPDetailsPaneProps) {
  const hasOutstandingBalance: boolean = useHasUnpaidBalance(unpaidBalance?.amount);
  const canOrder = useCanOrder(subscription);

  return (
    <div>
      {hasOutstandingBalance &&
      unpaidBalance &&
      unpaidBalance.amount &&
      unpaidBalance.paymentMethodConfiguration ? (
        <CannotOrderPayOverdueBalance
          shouldUseStripe={unpaidBalance.shouldUseStripe}
          stripeConfig={unpaidBalance.stripeConfig}
          unpaidBalance={unpaidBalance.amount}
          paymentMethodConfiguration={unpaidBalance.paymentMethodConfiguration}
          ADYEN_ORIGIN_KEY={unpaidBalance.ADYEN_ORIGIN_KEY}
          ADYEN_JS_SRC={unpaidBalance.ADYEN_JS_SRC}
          SMARTYSTREETS_EMBEDDED_KEY={unpaidBalance.SMARTYSTREETS_EMBEDDED_KEY}
          paymentErrors={unpaidBalance.paymentErrors}
          updatePaymentErrors={unpaidBalance.updatePaymentErrors}
        />
      ) : null}
      {!hasOutstandingBalance && (
        <>
          {
            // Pickups
            canOrder ? (
              !subscription?.has_vehicle ? (
                <>
                  {shouldBreakoutInsurance ? (
                    <PDPBuildMonthlyPackage
                      vehiclePlans={vehiclePlans}
                      insurancePlans={insurancePlans}
                      mostPopularPlanMileageAmount={mostPopularPlanMileageAmount}
                      isOrderable={isOrderable}
                      errors={errors}
                      creditModalOpen={creditModalOpen}
                      handleCloseCreditModal={handleCloseCreditModal}
                      handleOpenCreditModal={handleOpenCreditModal}
                      creditScores={creditScores}
                      ageRanges={ageRanges}
                      selectedIsOver25={selectedIsOver25}
                      selectedCreditScoreId={selectedCreditScoreId}
                      selectedAgeRangeId={selectedAgeRangeId}
                      selectedMileagePlan={selectedMileagePlan}
                      selectedInsurancePlan={selectedInsurancePlan}
                      enableInsuranceChange={enableInsuranceChange}
                      fullstoryPlanEventDelegate={fullstoryPlanEventDelegate}
                      fullstoryContinueEventDelegate={fullstoryContinueEventDelegate}
                      fullstoryInsuranceEventDelegate={fullstoryInsuranceEventDelegate}
                      price_savings_1200_over_850={price_savings_1200_over_850}
                      price_per_mile_850_plan_for_1200_miles={
                        price_per_mile_850_plan_for_1200_miles
                      }
                      price_difference_850_vs_1200={price_difference_850_vs_1200}
                      mostPopularPlanMileagePercentage={mostPopularPlanMileagePercentage}
                    />
                  ) : (
                    <PDPSelectMonthlyPlan
                      vehiclePlans={vehiclePlans}
                      mostPopularPlanMileageAmount={mostPopularPlanMileageAmount}
                      isOrderable={isOrderable}
                      errors={errors}
                      creditModalOpen={creditModalOpen}
                      handleCloseCreditModal={handleCloseCreditModal}
                      handleOpenCreditModal={handleOpenCreditModal}
                      creditScores={creditScores}
                      ageRanges={ageRanges}
                      selectedIsOver25={selectedIsOver25}
                      selectedCreditScoreId={selectedCreditScoreId}
                      selectedAgeRangeId={selectedAgeRangeId}
                      selectedMileagePlan={selectedMileagePlan}
                      fullstoryPlanEventDelegate={fullstoryPlanEventDelegate}
                    />
                  )}
                </>
              ) : // Swaps
              subscription.can_swap && currentPlanMiles !== undefined ? (
                <SwapDetailsPane
                  subscription={subscription}
                  currentPlanMiles={currentPlanMiles}
                  currentInsurancePlanTier={currentInsurancePlanTier}
                  vehiclePlans={vehiclePlans}
                  insurancePlans={insurancePlans}
                  selectedMileagePlan={selectedMileagePlan}
                  planError={errors?.plan}
                  mostPopularPlanMileageAmount={mostPopularPlanMileageAmount}
                  shouldBreakoutInsurance={shouldBreakoutInsurance}
                  enableInsuranceChange={enableInsuranceChange}
                  fullstoryPlanEventDelegate={fullstoryPlanEventDelegate}
                  fullstoryContinueEventDelegate={fullstoryContinueEventDelegate}
                />
              ) : (
                // Note: This state should not be possible but is included for completeness
                <div className="px-8 md:px-7">
                  <h1>Your monthly plan</h1>
                  <Spacer variant="height24" />
                  <Form method="post">
                    <StatusBanner variant="warning" title="No plans found" />
                    <Spacer variant="height24" />
                    <LoaderButton
                      type="submit"
                      name="intent"
                      value="plan"
                      className="w-full"
                      disabled={!isOrderable}
                    >
                      Continue to schedule
                    </LoaderButton>
                  </Form>
                </div>
              )
            ) : null
          }

          {!canOrder && (
            <CannotOrder hasPendingReturn={!isEmpty(subscription?.upcoming_return)} />
          )}
        </>
      )}
    </div>
  );
}
